import api from '../api'

export interface Communication {
  _id: string
  type: 'email' | 'sms' | 'push' | 'webhook' | 'system'
  status: 'pending' | 'queued' | 'sent' | 'delivered' | 'failed' | 'bounced' | 'retry'
  recipient: {
    userId?: number
    email?: string
    phone?: string
    deviceToken?: string
    webhookUrl?: string
    name?: string
  }
  metadata: {
    subject?: string
    template?: string
    organizationId?: number
    branchId?: number
    environment?: 'development' | 'production' | 'staging' | 'test'
    isTest?: boolean
    isSimulated?: boolean
    priority?: 'low' | 'normal' | 'high' | 'urgent'
    tags?: string[]
    trigger?: string
    source?: string
  }
  content?: {
    html?: string
    text?: string
    data?: any
  }
  timestamps?: {
    queued?: string
    sent?: string
    delivered?: string
    failed?: string
  }
  provider?: string
  error?: {
    message?: string
    code?: string
    details?: any
  }
  retryCount?: number
  createdAt: string
  updatedAt: string
}

export interface CommunicationStats {
  total: number
  sent: number
  failed: number
  pending: number
  successRate?: number
}

export interface EnhancedStats {
  overall: CommunicationStats
  byEnvironment: Array<{
    _id: string
    count: number
    sent: number
    failed: number
  }>
  testVsProduction: {
    test: number
    production: number
    simulated: number
  }
  byProvider: Array<{
    _id: string
    count: number
    successRate: number
  }>
}

export interface DailySummary {
  date: string
  summary: {
    byType: Array<{
      _id: string
      count: number
      sent: number
      failed: number
    }>
    byEnvironment: Array<{
      _id: string
      count: number
      test: number
      simulated: number
    }>
    hourly: Array<{
      _id: number
      count: number
    }>
    topErrors: Array<{
      _id: string
      count: number
    }>
    total: Array<{
      count: number
      sent: number
      failed: number
      pending: number
    }>
  }
}

export interface CommunicationFilters {
  type?: string
  status?: string
  recipientEmail?: string
  recipientUserId?: number
  organizationId?: number
  branchId?: number
  environment?: string
  isTest?: boolean
  isSimulated?: boolean
  dateFrom?: string
  dateTo?: string
  hasError?: boolean
}

export interface PaginationParams {
  skip?: number
  limit?: number
}

const communicationsApi = {
  // List all communications with filters
  listCommunications: (filters?: CommunicationFilters, pagination?: PaginationParams) => {
    return api.get('/admin/communications', {
      params: { ...filters, ...pagination }
    })
  },

  // Get communication by ID
  getCommunicationById: (id: string) => {
    return api.get(`/admin/communications/${id}`)
  },

  // Search communications
  searchCommunications: (query: string, filters?: CommunicationFilters, pagination?: PaginationParams) => {
    return api.get('/admin/communications/search', {
      params: { q: query, ...filters, ...pagination }
    })
  },

  // Get basic statistics
  getCommunicationStats: (filters?: {
    type?: string
    organizationId?: number
    branchId?: number
    environment?: string
    dateFrom?: string
    dateTo?: string
  }) => {
    return api.get('/admin/communications/stats', { params: filters })
  },

  // Get enhanced statistics
  getEnhancedStats: (filters?: {
    type?: string
    organizationId?: number
    branchId?: number
    dateFrom?: string
    dateTo?: string
  }) => {
    return api.get('/admin/communications/stats/enhanced', { params: filters })
  },

  // Get recent communications
  getRecentCommunications: (limit: number = 100, filters?: {
    type?: string
    status?: string
    organizationId?: number
    environment?: string
  }) => {
    return api.get('/admin/communications/recent', {
      params: { limit, ...filters }
    })
  },

  // Get failed communications
  getFailedCommunications: (filters?: {
    organizationId?: number
    branchId?: number
    environment?: string
    dateFrom?: string
    dateTo?: string
  }, pagination?: PaginationParams) => {
    return api.get('/admin/communications/failures', {
      params: { ...filters, ...pagination }
    })
  },

  // Get test communications
  getTestCommunications: (filters?: {
    type?: string
    status?: string
    organizationId?: number
  }, pagination?: PaginationParams) => {
    return api.get('/admin/communications/test', {
      params: { ...filters, ...pagination }
    })
  },

  // Get production communications
  getProductionCommunications: (filters?: {
    type?: string
    status?: string
    organizationId?: number
  }, pagination?: PaginationParams) => {
    return api.get('/admin/communications/production', {
      params: { ...filters, ...pagination }
    })
  },

  // Get daily summary
  getDailySummary: (date?: string) => {
    return api.get('/admin/communications/summary/daily', {
      params: date ? { date } : undefined
    })
  },

  // Export communications
  exportCommunications: (format: 'csv' | 'json', filters?: CommunicationFilters) => {
    return api.get('/admin/communications/export', {
      params: { format, ...filters },
      responseType: format === 'csv' ? 'blob' : 'json'
    })
  },

  // Resend communication
  resendCommunication: (id: string) => {
    return api.post(`/admin/communications/${id}/resend`)
  },

  // Check health
  checkHealth: () => {
    return api.get('/admin/communications/health')
  },

  // Get filesystem structure info
  getFilesystemStructure: () => {
    return api.get('/admin/communications/filesystem/structure')
  },
}

export default communicationsApi